import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { ModeBadge } from "@/components/bits";
import {
  LayoutDashboard, Search, FileText, GitPullRequestArrow, Activity,
  Eye, History, Wallet, Brain, Bot, Target, GraduationCap, Settings as SettingsIcon, LogOut, ShieldCheck, Plug, Plug2, Menu, X, Sparkles, Users, CalendarDays,
  FlaskConical,
  Database, Crown } from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/research", label: "AI Research", icon: Search, testid: "nav-research" },
  { to: "/wordpress", label: "WordPress Content", icon: FileText, testid: "nav-wordpress" },
  { to: "/review", label: "Change Review", icon: GitPullRequestArrow, testid: "nav-review" },
  { to: "/content-health", label: "Content Health", icon: Activity, testid: "nav-content-health" },
  { to: "/watchlist", label: "Watchlist", icon: Eye, testid: "nav-watchlist" },
  { to: "/update-intel", label: "Update Intelligence", icon: Sparkles, testid: "nav-update-intel" },
  { to: "/content-intel", label: "Content Intelligence", icon: Activity, testid: "nav-content-intel" },
  { to: "/history", label: "Update History", icon: History, testid: "nav-history" },
  { to: "/chief-editor", label: "Chief AI Editor", icon: Crown, testid: "nav-chief-editor" },
  { to: "/brain", label: "Editorial Brain", icon: Brain, testid: "nav-brain" },
  { to: "/opportunities", label: "Opportunities", icon: Target, testid: "nav-opportunities" },
  { to: "/exam-intelligence", label: "Exam Intelligence", icon: GraduationCap, testid: "nav-exam-intel" },
  { to: "/brain-automation", label: "Brain Automation", icon: Bot, testid: "nav-brain-automation" },
  { to: "/finance", label: "Finance", icon: Wallet, testid: "nav-finance", manage: true },
  { to: "/students", label: "Students", icon: Users, testid: "nav-students", manage: true },
  { to: "/test-students", label: "Test Students", icon: FlaskConical, testid: "nav-test-students", manage: true },
  { to: "/ezap-2027", label: "EZAP 2027", icon: Database, testid: "nav-ezap-2027" },
  { to: "/daily-results", label: "Daily Results", icon: CalendarDays, testid: "nav-daily-results", manage: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout, mode, wp } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const roles = (user?.roles || []).join(" · ");

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="flex min-h-screen bg-[#FBFBF9]">
      {/* Mobile overlay */}
      {mobileOpen && <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={() => setMobileOpen(false)} />}
      {/* Sidebar */}
      <aside className={`fixed md:static z-50 w-64 flex flex-col justify-between bg-slate-900 text-slate-200 min-h-screen transition-transform duration-300 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}>
        <div>
          <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-blue-400" />
              <div>
                <div className="font-serif text-lg font-bold leading-none text-white">EntranceZone</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">Editorial Intelligence</div>
              </div>
            </div>
            <button className="md:hidden text-slate-400" onClick={() => setMobileOpen(false)} data-testid="close-mobile-nav"><X className="h-5 w-5" /></button>
          </div>
          <nav className="p-3 space-y-1">
            {NAV.filter((n) => !n.manage || user?.permissions?.manage).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={n.testid}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  }`
                }
              >
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="p-4 border-t border-slate-800">
          <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
          <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-3">{roles}</div>
          <button
            data-testid="logout-btn"
            onClick={doLogout}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-300 hover:bg-slate-800"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-slate-200 bg-white/90 px-4 md:px-8 py-3 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <button className="md:hidden text-slate-700" onClick={() => setMobileOpen(true)} data-testid="open-mobile-nav"><Menu className="h-5 w-5" /></button>
            <ModeBadge mode={mode} />
          </div>
          <div className="flex items-center gap-3">
            {wp?.connected ? (
              <span data-testid="wp-connection-status-badge" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                <Plug className="h-3.5 w-3.5" /> WP Connected
              </span>
            ) : (
              <span data-testid="wp-connection-status-badge" className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-500">
                <Plug2 className="h-3.5 w-3.5" /> WP Not Connected
              </span>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto ez-scroll p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
